/**
 * BrandSmartImportPage.tsx
 *
 * 3-step flow matching the Flutter app:
 *   0  Lookup — auto-fetch on mount + inline "Add from another account"
 *   1  Select — all purchases merged, per-asset home picker
 *   2  Done   — success summary
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import {
  ArrowLeft, Check, Mail, Phone, MapPin,
  Home as HomeIcon, PlusCircle, Package, RefreshCw, Loader2,
  Search, ChevronRight, UserCheck, Shield, ShoppingBag, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { useAuthStore } from '../../store/authStore';
import { useProperty, Property } from '../../contexts/PropertyContext';
import {
  brandsmartLookup,
  brandsmartImport,
  brandsmartSendOtp,
  brandsmartVerifyOtp,
  brandsmartAutoLookup,
  BrandSmartLookupPurchase,
  BrandSmartLookupResponseData,
  BrandSmartImportItem,
  BrandSmartImportResponseData,
} from '../../services/brandsmart.api';
import { lookupProductImage, toAbsoluteImageUrl } from '../../services/asset.api';

// ─── Types ──────────────────────────────────────────────────────────

interface PurchaseResolution {
  targetHomeId: string | undefined;
  createNewHome: boolean;
}

// ─── Constants ──────────────────────────────────────────────────────

const OTP_LENGTH = 6;
const RESEND_SECONDS = 30;

const inputCls = cn(
  'flex h-11 w-full rounded-lg border bg-white px-4 py-2 text-sm transition-all',
  'placeholder:text-slate-400 text-slate-900',
  'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
  'border-slate-200 hover:border-slate-300',
  'disabled:opacity-50 disabled:pointer-events-none',
);

// ─── Helpers ────────────────────────────────────────────────────────

function maskEmail(email: string) {
  const [local, domain] = email.split('@');
  if (!domain) return email;
  return `${local.slice(0, Math.min(3, local.length))}****@${domain}`;
}
function maskPhone(phone: string) {
  const d = phone.replace(/\D/g, '');
  return d.length < 4 ? phone : `+*** ***-***-${d.slice(-4)}`;
}
function effectiveWarrantyStatus(preview: { manufacturer: string; extended: string }): string {
  if (preview.manufacturer === 'active' || preview.extended === 'active') return 'active';
  if (preview.manufacturer === 'expired' || preview.extended === 'expired') return 'expired';
  return 'none';
}
function WarrantyBadge({ type: _wt, status, expiry }: { type: 'manufacturer' | 'extended'; status: string; expiry?: string | null }) {
  const isActive = status === 'active';
  const isExpired = status === 'expired';
  const label = _wt === 'manufacturer' ? 'Manufacturer' : 'Extended';
  const bg = isActive ? 'bg-green-50 border-green-200 text-green-700' : isExpired ? 'bg-red-50 border-red-200 text-red-600' : 'bg-slate-50 border-slate-200 text-slate-400';
  const dot = isActive ? 'bg-green-500' : isExpired ? 'bg-red-500' : 'bg-slate-300';
  return (
    <span className={'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ' + bg}>
      <span className={'h-1.5 w-1.5 rounded-full ' + dot} />
      {label}
      {isActive && expiry && <span className="opacity-70"> \u00B7 {new Date(expiry).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
    </span>
  );
}
function formatPrice(price: number | undefined | null) {
  if (price == null) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price);
}

function addressMatchesHome(p: BrandSmartLookupPurchase, h: Property): boolean {
  const n = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();
  return n(p.deliveryAddress) === n(h.address) && n(p.deliveryCity) === n(h.city) && n(p.deliveryState) === n(h.state) && n(p.deliveryZip) === n(h.zip);
}

// ─── Component ──────────────────────────────────────────────────────

export default function BrandSmartImportPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('brandsmart');
  const { user } = useAuthStore();
  const { properties, refreshProperties } = useProperty();

  // Flow
  const [slide, setSlide] = useState(0);
  const [showDoneLoader, setShowDoneLoader] = useState(false);

  // Auto-fetch
  const [isFetchingOwn, setIsFetchingOwn] = useState(true);
  const [ownPurchases, setOwnPurchases] = useState<BrandSmartLookupPurchase[]>([]);
  const [ownFetchError, setOwnFetchError] = useState('');

  // Alt-contact OTP
  const [showAltSection, setShowAltSection] = useState(false);
  const [bContact, setBContact] = useState('');
  const [bType, setBType] = useState<'email' | 'phone'>('email');
  const [bOtpSent, setBOtpSent] = useState(false);
  const [bSending, setBSending] = useState(false);
  const [bOtpDigits, setBOtpDigits] = useState<string[]>(new Array(OTP_LENGTH).fill(''));
  const [bVerifying, setBVerifying] = useState(false);
  const [bOtpError, setBOtpError] = useState('');
  const [bPurchases, setBPurchases] = useState<BrandSmartLookupPurchase[]>([]);
  const [bVerified, setBVerified] = useState(false);
  const [resendSec, setResendSec] = useState(RESEND_SECONDS);
  const bOtpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Merged selection
  const [purchases, setPurchases] = useState<BrandSmartLookupPurchase[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [purchaseSources, setPurchaseSources] = useState<Map<string, 'own' | 'alt'>>(new Map());
  const [inlineResolutions, setInlineResolutions] = useState<Map<string, PurchaseResolution>>(new Map());
  const [serpImportMap, setSerpImportMap] = useState<Map<string, string>>(new Map());
  const serpFetchingRef = useRef<Set<string>>(new Set());

  // Import
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importResult, setImportResult] = useState<BrandSmartImportResponseData | null>(null);

  // ── Auto-fetch on mount ───────────────────────────────────────────
  useEffect(() => {
    if (!user?.email) return;
    setIsFetchingOwn(true);
    brandsmartAutoLookup()
      .then((res) => setOwnPurchases(res.purchases))
      .catch((err: unknown) => setOwnFetchError(err instanceof Error ? err.message : t('importPage.step0.lookupFailed')))
      .finally(() => setIsFetchingOwn(false));
  }, [user?.email]); // eslint-disable-line

  useEffect(() => {
    if (!bOtpSent || resendSec <= 0) return;
    const t = setTimeout(() => setResendSec((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [bOtpSent, resendSec]);

  // SERP image
  const handleImageError = React.useCallback(async (p: BrandSmartLookupPurchase) => {
    if (serpFetchingRef.current.has(p.id)) return;
    if (!p.brand && !p.productName) return;
    serpFetchingRef.current.add(p.id);
    try {
      const r = await lookupProductImage(p.brand || '', p.model || '', p.subCategory || p.category || '');
      if (r?.imageUrl) setSerpImportMap(prev => new Map(prev).set(p.id, r.imageUrl));
    } catch { /* */ } finally { serpFetchingRef.current.delete(p.id); }
  }, []);

  function computeAutoResolutions(ps: BrandSmartLookupPurchase[]) {
    const m = new Map<string, PurchaseResolution>();
    for (const p of ps) { const h = properties.find(x => addressMatchesHome(p, x)); if (h) m.set(p.id, { targetHomeId: h.id, createNewHome: false }); }
    return m;
  }

  // ── OTP handlers ──────────────────────────────────────────────────
  const handleBSendOtp = async () => {
    const c = bContact.trim();
    if (!c) return;
    if (bType === 'email' && user?.email && c.toLowerCase() === user.email.toLowerCase()) {
      toast.error(t('importPage.step2.sameEmailError')); return;
    }
    setBSending(true); setBOtpError('');
    try {
      await brandsmartSendOtp({ identifier: c });
      setBOtpDigits(new Array(OTP_LENGTH).fill(''));
      setBOtpSent(true); setResendSec(RESEND_SECONDS);
      setTimeout(() => bOtpRefs.current[0]?.focus(), 100);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : t('importPage.step2.sendCodeError'));
    } finally { setBSending(false); }
  };

  const handleBResend = async () => {
    setBSending(true); setBOtpError('');
    try {
      await brandsmartSendOtp({ identifier: bContact.trim() });
      setBOtpDigits(new Array(OTP_LENGTH).fill('')); setResendSec(RESEND_SECONDS);
      toast.success(t('importPage.step2.newCodeSent')); bOtpRefs.current[0]?.focus();
    } catch { toast.error(t('importPage.step2.resendError')); }
    finally { setBSending(false); }
  };

  function focusOtp(i: number) { bOtpRefs.current[Math.max(0, Math.min(OTP_LENGTH - 1, i))]?.focus(); }

  const handleBOtpDigit = (idx: number, val: string) => {
    setBOtpError('');
    if (val.length > 1) {
      const clean = val.replace(/\D/g, '').slice(0, OTP_LENGTH);
      setBOtpDigits(() => { const n = new Array(OTP_LENGTH).fill(''); clean.split('').forEach((c, i) => n[i] = c); return n; });
      focusOtp(Math.min(clean.length, OTP_LENGTH - 1));
      if (clean.length === OTP_LENGTH) setTimeout(() => { const cur = bOtpRefs.current.map(r => r?.value || '').join(''); if (cur.length === OTP_LENGTH) handleBVerify(); }, 100);
      return;
    }
    const clean = val.replace(/\D/g, '');
    const next = [...bOtpDigits]; next[idx] = clean; setBOtpDigits(next);
    if (clean && idx < OTP_LENGTH - 1) focusOtp(idx + 1);
  };

  const handleBOtpKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') { e.preventDefault();
      if (bOtpDigits[idx]) { const n = [...bOtpDigits]; n[idx] = ''; setBOtpDigits(n); setBOtpError(''); }
      else if (idx > 0) { focusOtp(idx - 1); const n = [...bOtpDigits]; n[idx - 1] = ''; setBOtpDigits(n); setBOtpError(''); }
    } else if (e.key === 'ArrowLeft' && idx > 0) { e.preventDefault(); focusOtp(idx - 1); }
    else if (e.key === 'ArrowRight' && idx < OTP_LENGTH - 1) { e.preventDefault(); focusOtp(idx + 1); }
  };

  const handleBOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault(); setBOtpError('');
    const pasted = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = new Array(OTP_LENGTH).fill(''); pasted.split('').forEach((c, i) => next[i] = c);
    setBOtpDigits(next); focusOtp(Math.min(pasted.length, OTP_LENGTH - 1));
    if (pasted.length === OTP_LENGTH) setTimeout(() => handleBVerify(), 100);
  };

  const handleBVerify = async () => {
    const otp = bOtpDigits.join('');
    if (otp.length < OTP_LENGTH) return;
    setBVerifying(true); setBOtpError('');
    try {
      const res = await brandsmartVerifyOtp({ identifier: bContact.trim(), otp });
      setBPurchases(res.purchases); setBVerified(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('importPage.step2.verifyError');
      setBOtpError(msg); setBOtpDigits(new Array(OTP_LENGTH).fill(''));
      setTimeout(() => bOtpRefs.current[0]?.focus(), 50);
    } finally { setBVerifying(false); }
  };

  const resetAltFlow = () => {
    setBContact(''); setBOtpSent(false); setBOtpDigits(new Array(OTP_LENGTH).fill(''));
    setBOtpError(''); setBVerified(false); setBPurchases([]);
  };

  // ── Single source of truth: what's selected to import? ────────────
  const ownSelectedIds = new Set(ownPurchases.map(p => p.id)); // all own are preselected
  const altSelectedIds = new Set(bPurchases.map(p => p.id));    // all alt are preselected
  const allPurchaseIds = [...ownPurchases.map(p => p.id), ...bPurchases.map(p => p.id)];
  const totalSelected = allPurchaseIds.length; // all are selected

  // ── Continue: merge & advance ─────────────────────────────────────
  const handleContinue = () => {
    const merged = [...ownPurchases, ...bPurchases.filter(p => !ownPurchases.some(o => o.id === p.id))];
    const sources = new Map<string, 'own' | 'alt'>();
    ownPurchases.forEach(p => sources.set(p.id, 'own'));
    bPurchases.forEach(p => sources.set(p.id, 'alt'));

    const ownRes = computeAutoResolutions(ownPurchases);
    const mergedRes = new Map(ownRes);
    bPurchases.forEach(p => {
      if (!mergedRes.has(p.id)) {
        const h = properties.find(x => addressMatchesHome(p, x));
        if (h) mergedRes.set(p.id, { targetHomeId: h.id, createNewHome: false });
      }
    });

    setPurchases(merged); setPurchaseSources(sources);
    setSelectedIds(new Set(merged.map(p => p.id)));
    setInlineResolutions(mergedRes);
    setSlide(1);
  };

  // ── Import ────────────────────────────────────────────────────────
  const handleDoImport = async () => {
    const selected = purchases.filter(p => selectedIds.has(p.id));
    if (selected.length === 0) return;
    const unresolved = selected.filter(p => { const r = inlineResolutions.get(p.id); return !r?.targetHomeId && !r?.createNewHome; });
    if (unresolved.length > 0) { setImportError(t('importPage.step3.assignAllError')); return; }

    setIsImporting(true); setImportError('');
    try {
      const items: BrandSmartImportItem[] = selected.map(p => {
        const r = inlineResolutions.get(p.id)!;
        return { purchaseId: p.id, targetHomeId: r.createNewHome ? null : (r.targetHomeId ?? null), createNewHome: r.createNewHome };
      });

      const ownItems = items.filter(i => purchaseSources.get(i.purchaseId) !== 'alt');
      const altItems = items.filter(i => purchaseSources.get(i.purchaseId) === 'alt');
      const results: BrandSmartImportResponseData[] = [];
      if (ownItems.length > 0) results.push(await brandsmartImport({ imports: ownItems }));
      if (altItems.length > 0) results.push(await brandsmartImport({ imports: altItems }));

      const combined: BrandSmartImportResponseData = {
        importedCount: results.reduce((s, r) => s + r.importedCount, 0),
        assets: results.flatMap(r => r.assets),
        newHomesCreated: results.reduce((s, r) => s + r.newHomesCreated, 0),
      };
      await refreshProperties();
      setImportResult(combined);
      setShowDoneLoader(true);
      setTimeout(() => { setShowDoneLoader(false); setSlide(2); }, 2000);
    } catch (err: unknown) {
      setImportError(err instanceof Error ? err.message : t('importPage.step3.importError'));
    } finally { setIsImporting(false); }
  };

  const bOtp = bOtpDigits.join('');

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      {showDoneLoader && (
        <div className="fixed inset-0 z-50 bg-white/95 flex flex-col items-center justify-center gap-4">
          <div className="relative">
            <div className="h-14 w-14 rounded-full border-2 border-slate-100 border-t-primary animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center"><Package className="h-6 w-6 text-slate-600" /></div>
          </div>
          <div className="text-center"><p className="text-sm font-semibold text-slate-900">{t('importPage.doneLoader.title')}</p><p className="text-xs text-slate-400 mt-1">{t('importPage.doneLoader.subtitle')}</p></div>
        </div>
      )}

      {/* Header */}
      <section className="bg-gradient-to-b from-background to-muted/20 border-b">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><ShoppingBag className="h-4 w-4 text-primary" /></div>
              <div className="min-w-0"><h1 className="text-xl font-bold text-foreground">{t('importPage.headerTitle')}</h1><p className="text-sm text-muted-foreground">{t('importPage.headerSubtitle')}</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SLIDE 0: Unified Lookup ─────────────────────────────────── */}
      {slide === 0 && (
        <main className="mx-auto max-w-2xl px-6 lg:px-8 py-8 space-y-8">
          {/* Section 1: Your Account */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <UserCheck className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">{t('importPage.step0.connectingTitle')}</h2>
                <p className="text-xs text-muted-foreground">{t('importPage.step0.matchingDesc')}</p>
              </div>
            </div>

            {isFetchingOwn ? (
              <div className="flex items-center gap-3 text-sm text-muted-foreground py-6">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                {t('importPage.step0.searchingRecords')}
              </div>
            ) : (
              <>
                {/* Connected badge */}
                <div className="flex items-center gap-2 px-3 py-2.5 mb-4 rounded-lg bg-emerald-50 border border-emerald-200">
                  <UserCheck className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                  <p className="text-sm text-emerald-700 font-medium">{t('importPage.step0.connectingTitle')}</p>
                  <Check className="h-4 w-4 text-emerald-500 ml-auto flex-shrink-0" strokeWidth={2.5} />
                </div>

                {/* Own purchases */}
                {ownPurchases.length > 0 ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-700">
                        {t('importPage.step0.purchasesFound', { count: ownPurchases.length })}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-semibold text-emerald-700">
                        <Check className="h-3 w-3" strokeWidth={3} />
                        {t('brandsmart:common.matched')}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {ownPurchases.map(p => {
                        const imgSrc = toAbsoluteImageUrl(serpImportMap.get(p.id) || p.productImageUrl || '');
                        return (
                          <div key={p.id} className="group relative flex items-start gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm transition-all">
                            <ProductImage src={imgSrc} alt={p.productName} className="h-16 w-16 rounded-lg border border-slate-100 flex-shrink-0" onError={() => handleImageError(p)} />
                            <div className="flex-1 min-w-0 space-y-1.5">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2">{p.productName}</p>
                                <Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                              </div>
                              <p className="text-xs text-slate-400">{[p.brand, p.model].filter(Boolean).join(' · ')}</p>
                              {/* Warranty tags */}
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <WarrantyBadge type="manufacturer" status={p.warrantyPreview.manufacturer} expiry={p.warrantyPreview.manufacturerExpiry} />
                                {p.warrantyPreview.extended !== 'none' && <WarrantyBadge type="extended" status={p.warrantyPreview.extended} expiry={p.warrantyPreview.extendedExpiry} />}
                              </div>
                              {/* Date + price */}
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                {p.purchaseDate && <span>{new Date(p.purchaseDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                                {p.purchasePrice != null && <span className="font-semibold text-slate-700">{formatPrice(p.purchasePrice)}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : ownFetchError ? (
                  <div className="text-center py-6 text-sm text-red-500">{ownFetchError}</div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 px-6 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50">
                    <div className="h-14 w-14 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center mb-4">
                      <Package className="h-6 w-6 text-slate-300" />
                    </div>
                    <p className="text-sm font-semibold text-slate-700">{t('importPage.step0.noPurchasesFoundTitle')}</p>
                    <p className="text-xs text-slate-400 mt-1 max-w-xs text-center">{t('importPage.step0.notFoundDesc')}</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">{t('common.or')}</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Section 2: Add from another account */}
          <div>
            <button
              onClick={() => { if (!showAltSection) setShowAltSection(true); else { resetAltFlow(); setShowAltSection(false); } }}
              className={cn(
                'w-full flex items-center gap-3 p-4 rounded-xl border-2 border-dashed transition-all text-left',
                showAltSection ? 'border-primary/30 bg-primary/[0.02]' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50',
              )}
            >
              <div className={cn('h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0', showAltSection ? 'bg-primary/10' : 'bg-slate-100')}>
                <PlusCircle className={cn('h-5 w-5', showAltSection ? 'text-primary' : 'text-slate-400')} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-700">{t('importPage.step2.heading')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('importPage.step2.subtitle')}</p>
              </div>
              <ChevronDown className={cn('h-5 w-5 text-slate-400 transition-transform', showAltSection && 'rotate-180')} />
            </button>

            {showAltSection && (
              <div className="mt-4 rounded-xl border bg-card p-5 space-y-4">
                {!bVerified && (
                  <>
                    {/* Tab switcher */}
                    <div className="flex rounded-lg border overflow-hidden w-fit">
                      {(['email', 'phone'] as const).map(tab => (
                        <button key={tab} onClick={() => setBType(tab)} disabled={bOtpSent}
                          className={cn('flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold transition-colors',
                            bType === tab ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:bg-slate-50')}>
                          {tab === 'email' ? <Mail className="h-3.5 w-3.5" /> : <Phone className="h-3.5 w-3.5" />}
                          {tab === 'email' ? t('importPage.step2.email') : t('importPage.step2.phone')}
                        </button>
                      ))}
                    </div>

                    {!bOtpSent ? (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input type={bType === 'email' ? 'email' : 'tel'} value={bContact}
                            onChange={e => setBContact(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleBSendOtp()}
                            placeholder={bType === 'email' ? t('importPage.step2.emailPlaceholder') : t('importPage.step2.phonePlaceholder')}
                            className={inputCls} disabled={bSending} />
                          <Button onClick={handleBSendOtp} disabled={bSending || !bContact.trim()}
                            className="rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground h-10 px-4 flex-shrink-0">
                            {bSending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('importPage.step2.sendCode')}
                          </Button>
                        </div>
                        <p className="text-[11px] text-slate-400">{t('importPage.step2.oneTimeHint')}</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-slate-600">
                            {t('importPage.step2.codeSentTo')} <strong>{bType === 'email' ? maskEmail(bContact) : maskPhone(bContact)}</strong>
                          </p>
                          <button onClick={() => { setBOtpSent(false); setBOtpDigits(new Array(OTP_LENGTH).fill('')); setBOtpError(''); }}
                            className="text-xs text-primary hover:underline mt-1">{t('importPage.step2.useDifferent')}</button>
                        </div>
                        {/* OTP boxes */}
                        <div className="flex gap-2 justify-center">
                          {bOtpDigits.map((d, i) => (
                            <input key={i} ref={el => { bOtpRefs.current[i] = el; }} type="text" inputMode="numeric" maxLength={1} value={d}
                              onChange={e => handleBOtpDigit(i, e.target.value)}
                              onKeyDown={e => handleBOtpKeyDown(i, e)}
                              onPaste={handleBOtpPaste}
                              className={cn('h-12 w-10 sm:w-11 text-center text-lg font-bold border-2 rounded-lg bg-white transition-all focus:outline-none',
                                bOtpError ? 'border-red-400 bg-red-50' : d ? 'border-primary bg-primary/5' : 'border-slate-200 focus:border-slate-400')} />
                          ))}
                        </div>
                        {bOtpError && <p className="text-xs text-red-500 text-center">{bOtpError}</p>}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-400">
                            {resendSec > 0 ? t('importPage.step2.resendIn', { seconds: resendSec }) : (
                              <button onClick={handleBResend} disabled={bSending} className="text-slate-600 font-semibold underline underline-offset-2 flex items-center gap-1">
                                <RefreshCw className={cn('h-3 w-3', bSending && 'animate-spin')} />{t('importPage.step2.resend')}
                              </button>
                            )}
                          </span>
                          <Button onClick={handleBVerify} disabled={bVerifying || bOtp.length < OTP_LENGTH}
                            className="rounded-lg bg-primary hover:bg-primary/90 text-white h-9 px-5">
                            {bVerifying ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t('importPage.step2.checking')}</> : t('importPage.step2.verify')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Alt-contact results */}
                {bVerified && bPurchases.length === 0 && (
                  <div className="text-center py-4 space-y-2">
                    <Search className="h-6 w-6 text-slate-300 mx-auto" />
                    <p className="text-sm font-semibold text-slate-800">{t('importPage.step2.noPurchasesTitle')}</p>
                    <p className="text-xs text-muted-foreground">{t('importPage.step2.noPurchasesBodyBefore')} <strong>{bType === 'email' ? maskEmail(bContact) : maskPhone(bContact)}</strong>.</p>
                    <button onClick={resetAltFlow} className="text-xs text-primary font-semibold hover:underline flex items-center gap-1 mx-auto">
                      <RefreshCw className="h-3 w-3" />{t('importPage.step2.tryDifferent')}
                    </button>
                  </div>
                )}

                {bVerified && bPurchases.length > 0 && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-4">
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700 mb-3">
                      <Check className="h-4 w-4" strokeWidth={3} />
                      {t('importPage.step2.foundItems', { count: bPurchases.length })}{' '}
                      <span className="font-normal text-slate-500">{t('importPage.step2.under', { contact: bType === 'email' ? maskEmail(bContact) : maskPhone(bContact) })}</span>
                    </p>
                    <div className="divide-y divide-emerald-100">
                      {bPurchases.map(p => {
                        const imgSrc = toAbsoluteImageUrl(serpImportMap.get(p.id) || p.productImageUrl || '');
                        return (
                          <div key={p.id} className="flex items-center gap-3 py-2.5">
                            <ProductImage src={imgSrc} alt={p.productName} className="h-10 w-10 rounded-lg border border-slate-100 flex-shrink-0" onError={() => handleImageError(p)} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 truncate">{p.productName}</p>
                              <p className="text-xs text-slate-400 truncate">{[p.brand, p.model].filter(Boolean).join(' · ')}</p>
                            </div>
                            <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" strokeWidth={2.5} />
                          </div>
                        );
                      })}
                    </div>
                    <button onClick={resetAltFlow} className="mt-3 text-xs text-slate-400 hover:text-slate-600 underline">{t('importPage.step2.change')}</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Single Continue button */}
          {!isFetchingOwn && (
            <button
              onClick={handleContinue}
              disabled={ownPurchases.length === 0 && (!bVerified || bPurchases.length === 0)}
              className="w-full h-12 rounded-xl inline-flex items-center justify-center gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm shadow-sm transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {totalSelected > 0
                ? `${t('common.continue')} — ${totalSelected} ${totalSelected === 1 ? 'item' : 'items'}`
                : t('importPage.step0.selectItems')}
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </main>
      )}

      {/* ─── SLIDE 1: Select Purchases ───────────────────────────────── */}
      {slide === 1 && (
        <main className="mx-auto max-w-3xl px-4 sm:px-6 py-6 space-y-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">{t('importPage.step1.heading')}</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                {purchases.length > 0 ? t('importPage.step1.subtitle', { count: purchases.length }) : t('importPage.step1.noPurchases')}
              </p>
            </div>
            <button onClick={() => setSelectedIds(prev => prev.size === purchases.length ? new Set() : new Set(purchases.map(p => p.id)))}
              className="text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50 transition-colors whitespace-nowrap flex-shrink-0">
              {selectedIds.size === purchases.length ? t('common.deselectAll') : t('common.selectAll')}
            </button>
          </div>

          {purchases.length === 0 ? (
            <div className="rounded-2xl border bg-card shadow-sm p-12 flex flex-col items-center justify-center gap-3">
              <Package className="h-10 w-10 text-slate-200" />
              <p className="text-sm text-muted-foreground">{t('importPage.step1.noPurchases')}</p>
            </div>
          ) : (
            <div className="rounded-2xl border bg-card shadow-sm p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {purchases.map(p => {
                  const isSel = selectedIds.has(p.id);
                  const res = inlineResolutions.get(p.id);
                  const imgSrc = toAbsoluteImageUrl(serpImportMap.get(p.id) || p.productImageUrl || '');
                  const isAlt = purchaseSources.get(p.id) === 'alt';
                  return (
                    <AssetSelectCard key={p.id} purchase={p} imgSrc={imgSrc} isSel={isSel}
                      isUnresolved={isSel && !res?.targetHomeId && !res?.createNewHome}
                      resolvedHome={res?.targetHomeId ? properties.find(h => h.id === res.targetHomeId) ?? null : null}
                      autoMatch={properties.find(h => addressMatchesHome(p, h)) ?? null}
                      createNewHome={res?.createNewHome ?? false}
                      properties={properties}
                      sourceLabel={isAlt ? (bType === 'email' ? maskEmail(bContact) : maskPhone(bContact)) : undefined}
                      onToggleSel={() => setSelectedIds(prev => { const n = new Set(prev); n.has(p.id) ? n.delete(p.id) : n.add(p.id); return n; })}
                      onAssign={(val) => {
                        if (val === '__new__') setInlineResolutions(prev => new Map(prev).set(p.id, { targetHomeId: undefined, createNewHome: true }));
                        else setInlineResolutions(prev => new Map(prev).set(p.id, { targetHomeId: val, createNewHome: false }));
                      }}
                      onClearAssignment={() => setInlineResolutions(prev => { const n = new Map(prev); n.delete(p.id); return n; })}
                      onImageError={() => { if (!serpImportMap.get(p.id)) handleImageError(p); }} />
                  );
                })}
              </div>
            </div>
          )}

          {importError && <p className="text-sm text-red-500 text-center">{importError}</p>}

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setSlide(0)} className="px-5 h-10 rounded-lg text-sm">{t('common.back')}</Button>
            <button onClick={handleDoImport} disabled={isImporting || selectedIds.size === 0}
              className="inline-flex items-center gap-2 px-6 h-10 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm shadow-sm transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed">
              {isImporting ? <><Loader2 className="h-4 w-4 animate-spin" />{t('importPage.step3.importing')}</> : t('importPage.step3.importWithCount', { count: selectedIds.size })}
            </button>
          </div>
        </main>
      )}

      {/* ─── SLIDE 2: Success ────────────────────────────────────────── */}
      {slide === 2 && importResult && (
        <main className="mx-auto max-w-2xl px-4 sm:px-6 py-6 space-y-4">
          <div className="rounded-2xl border bg-card shadow-sm p-8 flex flex-col items-center text-center gap-3">
            <div className="h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center"><Check className="h-8 w-8 text-emerald-600" strokeWidth={2.5} /></div>
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">{t('importPage.step3.doneTitle')}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {t('importPage.step3.doneSubtitle', { count: importResult.importedCount })}
                {importResult.newHomesCreated > 0 && t('importPage.step3.newHomesCreated', { count: importResult.newHomesCreated })}
              </p>
            </div>
          </div>

          {importResult.assets.length > 0 && (
            <div className="rounded-2xl border bg-card shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-900">{t('importPage.step3.importedItems')}</span>
                <span className="text-xs text-slate-400">{importResult.assets.length}</span>
              </div>
              <div className="divide-y divide-slate-100">
                {importResult.assets.map(a => {
                  const purchase = purchases.find(p => p.productName === a.name);
                  const imgSrc = toAbsoluteImageUrl((purchase ? serpImportMap.get(purchase.id) : undefined) || purchase?.productImageUrl || '');
                  const resolution = purchase ? inlineResolutions.get(purchase.id) : undefined;
                  const homeName = resolution?.createNewHome
                    ? t('importPage.card.newHome')
                    : resolution?.targetHomeId
                      ? (properties.find(h => h.id === resolution.targetHomeId)?.name ?? properties.find(h => h.id === resolution.targetHomeId)?.address ?? t('importPage.card.newHome'))
                      : null;
                  return (
                    <div key={a.id} className="flex items-center gap-3 px-6 py-3.5">
                      <ProductImage src={imgSrc} alt={a.name} className="h-11 w-11 rounded-xl border border-slate-100 flex-shrink-0" />
                      <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-slate-900 truncate">{a.name}</p>{purchase && <p className="text-xs text-slate-400 truncate mt-0.5">{[purchase.brand, purchase.model].filter(Boolean).join(' · ')}</p>}
                        {homeName && (
                          <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                            <HomeIcon className="h-3 w-3 text-slate-400" />
                            {homeName}
                          </p>
                        )}
                      </div>
                      <span className="text-[11px] font-semibold flex-shrink-0 flex items-center gap-1 text-slate-500"><Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={3} />{t('importPage.step3.added')}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-start gap-3 rounded-2xl bg-slate-50 border border-slate-100 px-5 py-4">
            <Shield className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-600 leading-relaxed">{t('importPage.step3.warrantySaved')}</p>
          </div>

          <div className="flex justify-end"><button onClick={() => navigate('/assets')} className="inline-flex items-center gap-1 h-10 px-6 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm shadow-sm transition-colors duration-150">{t('importPage.step3.viewAssets')} <ChevronRight className="h-4 w-4" /></button></div>
        </main>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────

interface ProductImageProps { src: string; alt: string; className?: string; onError?: () => void; }

function ProductImage({ src, alt, className, onError }: Readonly<ProductImageProps>) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  useEffect(() => { setLoaded(false); setErrored(false); }, [src]);
  if (!src || errored) return <div className={cn('bg-muted/40 flex items-center justify-center', className)}><Package className="h-1/3 w-1/3 text-slate-300" /></div>;
  return (
    <div className={cn('relative bg-muted/30 overflow-hidden', className)}>
      {!loaded && <div className="absolute inset-0 animate-pulse bg-slate-100" />}
      <img key={src} src={src} alt={alt} referrerPolicy="no-referrer" loading="lazy"
        onLoad={() => setLoaded(true)} onError={() => { setErrored(true); onError?.(); }}
        className={cn('h-full w-full object-contain p-2 transition-opacity duration-200', loaded ? 'opacity-100' : 'opacity-0')} />
    </div>
  );
}

interface AssetSelectCardProps {
  purchase: BrandSmartLookupPurchase; imgSrc: string; isSel: boolean; isUnresolved: boolean;
  resolvedHome: Property | null; autoMatch: Property | null; createNewHome: boolean;
  properties: Property[]; sourceLabel?: string;
  onToggleSel: () => void; onAssign: (val: string) => void; onClearAssignment: () => void; onImageError: () => void;
}

function AssetSelectCard({ purchase: p, imgSrc, isSel, isUnresolved, resolvedHome, autoMatch, createNewHome, properties, sourceLabel, onToggleSel, onAssign, onClearAssignment, onImageError }: Readonly<AssetSelectCardProps>) {
  const { t } = useTranslation('brandsmart');
  const [popOpen, setPopOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (!popOpen) return; const h = (e: MouseEvent) => { if (popRef.current && !popRef.current.contains(e.target as Node)) setPopOpen(false); }; document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h); }, [popOpen]);

  return (
    <div role="button" tabIndex={0} aria-pressed={isSel} onClick={onToggleSel} onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onToggleSel(); } }}
      className={cn('group relative flex items-start gap-3 p-3 rounded-xl border text-left transition-all duration-150 cursor-pointer',
        isSel ? 'border-primary/40 bg-primary/[0.03] ring-1 ring-primary/10' : 'border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50/60')}>
      <div className="flex-shrink-0"><ProductImage src={imgSrc} alt={p.productName} className="h-16 w-16 rounded-lg border border-slate-100" onError={onImageError} /></div>
      <div className="flex-1 min-w-0 py-0.5 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 line-clamp-2 leading-snug">{p.productName}</p>
            {sourceLabel && <p className="text-[10px] text-slate-400 mt-0.5">from {sourceLabel}</p>}
          </div>
          <div className={cn('h-5 w-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all', isSel ? 'bg-primary border-primary' : 'border-slate-300 bg-white')}>
            {isSel && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            <WarrantyBadge type="manufacturer" status={p.warrantyPreview.manufacturer} expiry={p.warrantyPreview.manufacturerExpiry} />
            {p.warrantyPreview.extended !== 'none' && (
              <WarrantyBadge type="extended" status={p.warrantyPreview.extended} expiry={p.warrantyPreview.extendedExpiry} />
            )}
          </div>
          {/* Price + Date */}
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {p.purchaseDate && <span>{new Date(p.purchaseDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
            {p.purchasePrice != null && <span className="font-semibold text-slate-700">{formatPrice(p.purchasePrice)}</span>}
          </div>
        </div>
        {isSel && (resolvedHome || createNewHome) && (
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary/5 border border-primary/15 w-fit max-w-full">
            {resolvedHome ? <HomeIcon className="h-3 w-3 text-primary flex-shrink-0" /> : <PlusCircle className="h-3 w-3 text-primary flex-shrink-0" />}
            <span className="text-[11px] font-medium text-primary truncate">{resolvedHome ? resolvedHome.name : t('importPage.card.newHome')}</span>
            {(!autoMatch || createNewHome) && <button type="button" onClick={e => { e.stopPropagation(); onClearAssignment(); }} className="text-primary/50 hover:text-primary text-sm leading-none ml-0.5">{'×'}</button>}
          </div>
        )}
        {isUnresolved && (
          <div ref={popRef} className="relative mt-1" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} role="presentation">
            <button type="button" onClick={() => setPopOpen(o => !o)}
              className={cn('flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-colors',
                popOpen ? 'border-amber-400 bg-amber-100 text-amber-800' : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100')}>
              <HomeIcon className="h-3 w-3 flex-shrink-0" />{t('importPage.card.assignHome')}<ChevronRight className={cn('h-3 w-3 flex-shrink-0 transition-transform', popOpen ? '-rotate-90' : 'rotate-90')} />
            </button>
            {popOpen && (
              <div className="absolute bottom-full left-0 mb-2 w-64 z-50 rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
                {(p.deliveryCity || p.deliveryState) && (
                  <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-100 bg-slate-50">
                    <MapPin className="h-3 w-3 text-amber-500 flex-shrink-0" />
                    <p className="text-[11px] text-slate-500">{t('importPage.card.deliveredTo')} <span className="font-semibold text-slate-700">{[p.deliveryCity, p.deliveryState].filter(Boolean).join(', ')}</span></p>
                  </div>
                )}
                <div className="px-3 pt-2.5 pb-1"><p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{t('importPage.card.whichHome')}</p></div>
                <div className="px-2 pb-2 flex flex-col gap-0.5">
                  {properties.map(home => (
                    <button key={home.id} type="button" onClick={() => { onAssign(home.id); setPopOpen(false); }}
                      className="flex items-center gap-2.5 w-full text-left px-2.5 py-2 rounded-lg text-slate-700 hover:bg-primary/5 hover:text-primary transition-colors group">
                      <HomeIcon className="h-3.5 w-3.5 text-slate-400 group-hover:text-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0"><p className="text-[11px] font-semibold leading-tight truncate">{home.name}</p>{home.city && <p className="text-[10px] text-slate-400 truncate">{home.city}{home.state ? `, ${home.state}` : ''}</p>}</div>
                      <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-primary flex-shrink-0" />
                    </button>
                  ))}
                  <div className="my-1 border-t border-slate-100" />
                  <button type="button" onClick={() => { onAssign('__new__'); setPopOpen(false); }}
                    className="flex items-center gap-2.5 w-full text-left px-2.5 py-2 rounded-lg text-slate-500 hover:bg-primary/5 hover:text-primary transition-colors group">
                    <PlusCircle className="h-3.5 w-3.5 text-slate-400 group-hover:text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0"><p className="text-[11px] font-semibold leading-tight">{t('importPage.card.addAsNewHome')}</p><p className="text-[10px] text-slate-400">{t('importPage.card.createNewProperty')}</p></div>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
